import {
  Behaviour,
  EventList,
  GameObject,
  serializable,
  Renderer,
  Animator,
} from "@needle-tools/engine";
import { CheckpointCounter } from "./CheckpointCounter";
import { StateManager } from "./GameStateManager";
import { GameStateEvents, RaceOrderEvents } from "./Consts";
import { Commentary } from "./Commentary";
import { EventData } from "./EventData";
import { EventDataEvents } from "./Consts";
import { RacerKitManager } from "./RacerKitManager";
import { Texture } from "three";
import { ISubscribableEvent } from "./ISubscribableEvent";
import { WheelController } from "./WheelContoller";

export class RacerEntry {
  @serializable(GameObject)
  public racerTransform;
  @serializable(Renderer)
  public jockeyRenderer;
  @serializable(Renderer)
  public iconRenderer;
  @serializable()
  public racerSprite;
  @serializable()
  public racerNumber;
  @serializable()
  public racerName;
}

/*
This class manages the order of the racers 
*/
export class RaceOrderManager extends Behaviour implements ISubscribableEvent {
  private static instance: RaceOrderManager | null = null;

  public static getInstance(): RaceOrderManager {
    if (RaceOrderManager.instance === null) {
      RaceOrderManager.instance = new RaceOrderManager();
    }
    return RaceOrderManager.instance;
  }

  awake(): void {
    //set instance to this
    RaceOrderManager.instance = this;
  }

  @serializable(RacerEntry)
  public racers: RacerEntry[] = [];

  @serializable(RacerKitManager)
  public kitManager: RacerKitManager | null = null;

  private previousRacerEntries: RacerEntry[] = [];

  @serializable(CheckpointCounter)
  public checkpoints: CheckpointCounter[] = [];

  private raceFinished: boolean = false;

  public useLaps: boolean = false;

  private numberOfLaps: number = 0;
  private currentLap: number = 0;

  private commentary?: Commentary;

  @serializable(EventList)
  public onRaceOrderUpdated?: EventList; //UIManager.updateraceorderIcons was assigned here in inspector but has been replaced with non Unity UI

  private onRaceOrderUpdatedJS: (() => void)[] = [];

  @serializable(EventList)
  public onRacerKitsAssigned?: EventList;

  @serializable(WheelController)
  public wheelControllerArray : WheelController[] = [];

  @serializable(Animator)
  public steeringWheelAnimatorArray : Animator[] = [];

  private bindEvents() {
    this.PreRaceSetup = this.PreRaceSetup.bind(this);
    this.assignRacerData = this.assignRacerData.bind(this);
  }

  private subscribeToEvents() {
    StateManager.getInstance().subscribeToEvent(
      GameStateEvents.ON_ENTER_RACE_IN_PROGRESS_STATE,
      this.PreRaceSetup
    ); //Triggered in RaceInProgress in case we join a running event

    const eventData = EventData.getInstance();
    eventData.subscribeToEvent(
      EventDataEvents.ON_DATA_ASSIGNED,
      this.assignRacerData
    );
  }

  private unsubscribeFromEvents() {
    StateManager.getInstance().unsubscribeFromEvent(
      GameStateEvents.ON_ENTER_RACE_IN_PROGRESS_STATE,
      this.PreRaceSetup
    );

    const eventData = EventData.getInstance();
    eventData.unsubscribeFromEvent(
      EventDataEvents.ON_DATA_ASSIGNED,
      this.assignRacerData
    );
  }

  start() {
    this.bindEvents();
    this.subscribeToEvents();
    this.commentary = Commentary.getInstance();
  }

  onDestroy(): void {
    this.unsubscribeFromEvents();
  }

  private assignRacerData() {
    let eventData = EventData.getInstance();
    let competitors = eventData.getCompetitors();

    console.log(eventData);

    console.log(competitors);

    for (let i = 0; i < competitors.length; i++) {
      this.racers[i].racerName = competitors[i].getName();
    }
    this.assignKitsToRacers();
  }

  /**
   * Calls the raceKitManager to assign kits to the racers based on racer name (main identifier for racer)
   */
  public assignKitsToRacers(): void {
    if (!this.kitManager) {
      console.error("RacerKitManager is null");
      return;
    }

    // this.racers.forEach(entry => {
    //     this.kitManager?.assignRacerKit(entry);
    // });

    for (let i = 0; i < this.racers.length; i++) {
      this.kitManager?.assignRacerKit(this.racers[i]);
    }

    this.onRacerKitsAssigned?.invoke();
  }

  update() {}

  private PreRaceSetup(): void {
    this.SetupCheckpoints();
  }

  public IsRaceComplete(): boolean {
    if (this.currentLap >= this.numberOfLaps) {
      return true;
    } else {
      return false;
    }
  }

  public ReturnRaceLeader(): RacerEntry {
    return this.racers[0];
  }

  public ReturnRacers(): RacerEntry[] {
    return this.racers;
  }

  public ReturnRacersParsed(): {
    racerName: string;
    racerNumber: any;
    racerTexture: Texture;
  }[] {
    // When detail is true, return detailed information
    return this.racers.map((racer) => ({
      racerName: racer.racerName,
      racerNumber: racer.racerNumber,
      racerTexture: this.kitManager?.getRacerFlagTexture(
        racer.racerName
      ) as Texture,
    }));
  }

  public ReturnRacerInSpecificPosition(position: number): RacerEntry {
    return this.racers[position];
  }

  public UpdateRaceOrder(latestCheckpoint: CheckpointCounter): void {
    let latestRaceOrder = latestCheckpoint.GetRacerArray();
    // currentRacerOrder will contain names of racers in the order they reached the last checkpoint so for example:
    // currentRacerOrder[0] = "1", currentRacerOrder[1] = "3", currentRacerOrder[2] = "2", currentRacerOrder[3] = "4", currentRacerOrder[4] = "5", currentRacerOrder[5] = "6"

    this.previousRacerEntries = this.racers;

    //Order the racers array based on the order of the currentRacerOrder array
    var tempRacers: RacerEntry[] = [];

    //TODO - If this is the last checkpoint, set the order to be the same as the arrival order
    if (latestCheckpoint.isFinishLine) {
      console.log("Finish Line Reached");
      const eventData = EventData.getInstance();
      const arrivalFull = eventData.getArrivalOrderFull();
      console.log("The arrival order is ", arrivalFull);

      // order racers based on arrival order
      for (let i = 0; i < arrivalFull.length; i++) {
        const racerEntry = this.ReturnRacerEntryByNumber(arrivalFull[i]);
        if (racerEntry) {
          tempRacers.push(racerEntry);
        }
      }
    } else {
      for (let i = 0; i < latestRaceOrder.length; i++) {
        for (let j = 0; j < this.racers.length; j++) {
          if (latestRaceOrder[i].racerNumber == this.racers[j].racerNumber) {
            tempRacers.push(this.racers[j]);
          }
        }
      }
    }

    this.racers = tempRacers;

    if (this.previousRacerEntries != null) {
      this.commentary?.processCommentaryStatement(
        this.previousRacerEntries,
        this.racers,
        latestCheckpoint.isFinishLine
      );
    }

    this.onRaceOrderUpdated!.invoke();
    this.onRaceOrderUpdatedJS.forEach((listener) => listener());

    this.dispatchEvent(
      new CustomEvent("customRaceOrder", { detail: this.ReturnRacersParsed() })
    );
  }

  public ReturnRacerEntryByNumber(racerNumber: number): RacerEntry | undefined {
    for (let i = 0; i < this.racers.length; i++) {
      if (this.racers[i].racerNumber == racerNumber) {
        return this.racers[i];
      }
    }
    return undefined;
  }

  public HandleRacerWheelSpin(racerIndex : number): void {

    for (let i = 0; i < this.wheelControllerArray.length; i++) {
      if(i == racerIndex){
        this.wheelControllerArray[i].rotateWheels(true);
      } else {
        this.wheelControllerArray[i].rotateWheels(false);
      }
    }
  }

  public ToggleAllSteeringWheels(enabled : boolean): void {
    for (let i = 0; i < this.steeringWheelAnimatorArray.length; i++) {
      this.steeringWheelAnimatorArray[i].setTrigger(enabled ? "Move" : "Stop");
    }
  }

  public ToggleSteeringWheelsBarActive(activeRacer: string): void {
    // Extract RacerX from "RacerXCam"
    const racerMatch = activeRacer.match(/(Racer\d+)/);
    if (!racerMatch) return; // Ensure we found a match
    const racerNumber = racerMatch[1].replace("Racer", ""); // Extract only the number

    for (let i = 0; i < this.steeringWheelAnimatorArray.length; i++) {
        const steeringWheelName = this.steeringWheelAnimatorArray[i].name; // e.g., "Formula1_Car1"

        // Check if the name ends with _CarX where X matches the racer number
        if (steeringWheelName.endsWith(`_Car${racerNumber}`)) {
            this.steeringWheelAnimatorArray[i].enabled = true;
        } else {
            this.steeringWheelAnimatorArray[i].enabled = false;
        }
    }
}



  private SetupCheckpoints(): void {
    //Set the "nextCheckpoint" variable of each checkpoint to the next checkpoint in the list
    for (let i = 0; i < this.checkpoints.length; i++) {
      if (this.checkpoints[i].getRaceOrderManager() == null) {
        this.checkpoints[i].setRaceOrderManager(this);
      }

      this.checkpoints[i].resetCheckpoint();

      if (i == this.checkpoints.length - 1) {
        if (this.useLaps == true) {
          this.checkpoints[i].nextCheckpoint = this.checkpoints[0].gameObject;
        } else {
          this.checkpoints[i].nextCheckpoint = this.checkpoints[i].gameObject; //Set it to itself as the race is over
        }
      } else {
        this.checkpoints[i].nextCheckpoint = this.checkpoints[i + 1].gameObject;
      }
    }
  }

  public subscribeToEvent(eventName: string, listener: () => void): void {
    if (eventName === RaceOrderEvents.ON_RACE_ORDER_UPDATED) {
      if (!this.onRaceOrderUpdatedJS.includes(listener)) {
        this.onRaceOrderUpdatedJS.push(listener);
      } else {
        console.log(
          `Listener already exists in onCommentaryUpdated array for ${listener}`
        );
      }
    } else {
      throw new Error("Event name not recognized.");
    }
  }

  public unsubscribeFromEvent(eventName: string, listener: () => void): void {
    if (eventName === RaceOrderEvents.ON_RACE_ORDER_UPDATED) {
      const index = this.onRaceOrderUpdatedJS.indexOf(listener);
      if (index !== -1) {
        this.onRaceOrderUpdatedJS.splice(index, 1);
      }
    } else {
      throw new Error("Event name not recognized.");
    }
  }
}
